import type { ReactElement } from "react";
import { classNames } from "@/react/helpers/twMergeWithCN";

interface LoadingLineProps {
  baseContainerClasses: string;
  className?: string;
}

export const LoadingLine = ({
  className,
  baseContainerClasses,
}: LoadingLineProps): ReactElement => (
  <div className={classNames(baseContainerClasses, "gap-4", className)}>
    <div className="bg-tertiary h-6 w-1/2 animate-pulse rounded-full" />
    <div className="flex h-64 flex-col gap-4">
      <div className="relative flex size-full flex-col">
        {/* Y-axis labels skeleton */}
        <div className="absolute left-0 top-0 flex h-full flex-col justify-between py-2">
          {Array.from({ length: 5 }, (_, index) => (
            <div
              key={`y-axis-${index}`}
              className="bg-tertiary h-3 w-8 animate-pulse rounded-full"
            />
          ))}
        </div>

        {/* Chart area with grid lines */}
        <div className="relative ml-12 mr-4 flex h-full flex-col justify-between">
          {/* Y-axis line */}
          <div className="bg-tertiary absolute left-0 top-0 h-full w-px animate-pulse" />
          {/* X-axis line */}
          <div className="bg-tertiary absolute bottom-0 left-0 h-px w-full animate-pulse" />
          {/* Horizontal grid lines */}
          {Array.from({ length: 4 }, (_, index) => (
            <div
              key={`grid-${index}`}
              className="bg-tertiary h-px w-full animate-pulse"
            />
          ))}

          {/* Data points skeleton */}
          <div className="absolute inset-0 mb-4 mt-2 flex items-end justify-between px-2">
            {Array.from({ length: 6 }, (_, index) => {
              // S-curve positions: slow start, fast middle, slower end
              const positions = [75, 70, 60, 45, 25, 15];
              return (
                <div
                  key={`point-${index}`}
                  className="bg-tertiary size-1.5 animate-pulse rounded-full opacity-40"
                  style={{
                    animationDelay: `${index * 0.12}s`,
                    marginBottom: `${positions[index]}px`,
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* X-axis labels skeleton */}
        <div className="ml-12 mr-4 mt-2 flex justify-between">
          {Array.from({ length: 6 }, (_, index) => (
            <div
              key={`x-axis-${index}`}
              className="bg-tertiary h-3 w-12 animate-pulse rounded-full"
            />
          ))}
        </div>
      </div>
    </div>

    {/* Legend skeleton */}
    <div className="flex flex-wrap justify-center gap-4">
      {Array.from({ length: 3 }, (_, index) => (
        <div key={`line-legend-${index}`} className="flex items-center gap-2">
          <div className="bg-tertiary size-3 animate-pulse rounded-full" />
          <div className="bg-tertiary h-4 w-16 animate-pulse rounded-full" />
        </div>
      ))}
    </div>
  </div>
);
